import authHeader from '@/backend/auth/auth-header'
import { useAuthAxios } from '@/backend/utils.js'
import {
  filterERPNotifications,
  parseUnreadCountPayload,
  parseMarkReadPayload,
  withERPNotificationSourceFilter,
} from '@/components/notifications/notificationLogic.mjs'

const isDev = process.env.NODE_ENV === 'development'

/**
 * Dev: прокси /message-service → message-service (vue.config.js).
 * Prod: VUE_APP_MESSAGE_SERVICE_URL (origin сервиса без хвоста /api),
 * либо тот же relative prefix /message-service за gateway.
 */
export function messageServiceBaseURL() {
  if (isDev) return '/message-service'
  const raw = (process.env.VUE_APP_MESSAGE_SERVICE_URL || '').replace(/\/$/, '')
  return raw || '/message-service'
}

function createClient() {
  const base = messageServiceBaseURL()
  return useAuthAxios({ baseURL: `${base}/api` })
}

/**
 * GET /api/messages/unread-count
 * @returns {Promise<number>}
 */
export async function fetchUnreadCount({ signal } = {}) {
  const api = createClient()
  const res = await api.get('/messages/unread-count', {
    params: withERPNotificationSourceFilter(),
    signal,
    headers: authHeader(),
  })
  return parseUnreadCountPayload(res.data)
}

/**
 * GET /api/messages?...
 * @returns {Promise<Array>}
 */
export async function fetchMessages(params = {}, { signal } = {}) {
  const api = createClient()
  const res = await api.get('/messages', {
    params: withERPNotificationSourceFilter(params),
    signal,
    headers: authHeader(),
  })
  return filterERPNotifications(res.data)
}

/**
 * POST /api/messages/read?...
 * Помечает непрочитанные сообщения аудитории как прочитанные.
 * @returns {Promise<number>} число помеченных
 */
export async function markMessagesAsRead(params = {}, { signal } = {}) {
  const api = createClient()
  const res = await api.post('/messages/read', {}, {
    params: withERPNotificationSourceFilter(params),
    signal,
    headers: authHeader(),
  })
  return parseMarkReadPayload(res.data)
}

/**
 * PUT /api/messages/{id}
 * Помечает одно сообщение прочитанным (is_read: true).
 * @returns {Promise<object>}
 */
export async function markMessageAsRead(messageId, { signal } = {}) {
  const id = Number(messageId)
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error('invalid message id')
  }
  const api = createClient()
  const res = await api.put(`/messages/${id}`, { is_read: true }, {
    signal,
    headers: authHeader(),
  })
  return res.data
}

/**
 * GET /api/channels/push
 * @returns {Promise<Array>}
 */
export async function fetchUserChannels(params = {}, { signal } = {}) {
  const api = createClient()
  const res = await api.get('/channels/push', {
    params,
    signal,
    headers: authHeader(),
  })
  return Array.isArray(res.data) ? res.data : []
}

/**
 * GET /api/settings/user
 * @returns {Promise<Array>}
 */
export async function fetchUserSettings({ signal } = {}) {
  const api = createClient()
  const res = await api.get('/settings/user', { signal, headers: authHeader() })
  return Array.isArray(res.data) ? res.data : []
}

/**
 * PUT /api/settings/{id}
 * @returns {Promise<object>}
 */
export async function updateChannelSettings(settingsId, payload) {
  const api = createClient()
  const res = await api.put(`/settings/${settingsId}`, payload, { headers: authHeader() })
  return res.data
}

/**
 * POST /api/settings
 * @returns {Promise<object>}
 */
export async function createChannelSettings(payload) {
  const api = createClient()
  const res = await api.post('/settings', payload, { headers: authHeader() })
  return res.data
}

/**
 * GET /api/push/vapid-public-key
 * @returns {Promise<{ public_key?: string, enabled?: boolean }>}
 */
export async function fetchVapidPublicKey({ signal } = {}) {
  const api = createClient()
  const res = await api.get('/push/vapid-public-key', { signal, headers: authHeader() })
  return res.data
}

/**
 * POST /api/push/subscriptions
 */
export async function savePushSubscription(payload) {
  const api = createClient()
  await api.post('/push/subscriptions', payload, { headers: authHeader() })
}

/**
 * DELETE /api/push/subscriptions
 */
export async function deletePushSubscription(payload) {
  const api = createClient()
  await api.delete('/push/subscriptions', { data: payload, headers: authHeader() })
}
